import React, { useContext, useState } from 'react';
import { useIntl } from 'react-intl-next';

import { cssMap, cx } from '@atlaskit/css';
import { Box, Inline } from '@atlaskit/primitives/compiled';
import Pressable from '@atlaskit/primitives/pressable';
import { token } from '@atlaskit/tokens';

import { EditorContextPanelContext } from '@confluence/editor-context-panel/entry-points/EditorContextPanelContext.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import {
	ANALYTICS_OPERATIONAL_SUBJECT,
	ANALYTICS_ORIGIN,
	ANALYTICS_UI_SUBJECT_ID,
} from '../../constants/snippetAnalytics.ts';
import { useSnippetAnalytics } from '../../hooks/analytics/useSnippetAnalytics.ts';
import { useInsertSnippet } from '../../hooks/panel/useInsertSnippet.ts';
import type { Snippet } from '../../types/snippet.ts';
import { SnippetPreview } from '../../preview/SnippetPreview.tsx';
import { useSnippetsActions } from '../../store/snippetsStore.ts';

import { useSnippetPopupPreview } from '../preview/SnippetPopupPreviewContext.tsx';
import { SnippetPreviewSkeleton } from '../skeleton/SnippetPreviewSkeleton.tsx';

import { messages } from './SnippetPanelItem.i18n.ts';
import { SnippetPanelItemContent } from './SnippetPanelItemContent.tsx';

const SNIPPET_PREVIEW_WIDTH = 140;
const SNIPPET_PREVIEW_HEIGHT = 80;
const EDITOR_PRE_SCALE_WIDTH = 800;
const PREVIEW_PADDING_BLOCK = token('space.075');
const PREVIEW_PADDING_INLINE = token('space.100');

const styles = cssMap({
	cardWrapper: {
		position: 'relative',
		display: 'flex',
		width: '100%',
		height: '100px',
		backgroundColor: 'transparent',
		borderRadius: token('radius.small'),
		'&:hover': {
			backgroundColor: token('elevation.surface.hovered'),
		},
	},
	cardWrapperActive: {
		backgroundColor: token('elevation.surface.hovered'),
	},
	cardButtonOverlay: {
		position: 'absolute',
		top: token('space.0'),
		right: token('space.0'),
		bottom: token('space.0'),
		left: token('space.0'),
		backgroundColor: 'transparent',
		textAlign: 'left',
	},
	cardContentLayer: {
		position: 'relative',
		width: '100%',
		zIndex: 1,
		pointerEvents: 'none',
		paddingLeft: token('space.100'),
		paddingRight: token('space.100'),
	},
	preview: {
		width: `${SNIPPET_PREVIEW_WIDTH}px`,
		minWidth: `${SNIPPET_PREVIEW_WIDTH}px`,
		height: `${SNIPPET_PREVIEW_HEIGHT}px`,
		borderRadius: token('radius.large'),
		border: `${token('border.width')} solid ${token('color.border')}`,
		overflow: 'hidden',
		backgroundColor: token('elevation.surface'),
		paddingBlock: PREVIEW_PADDING_BLOCK,
		paddingInline: PREVIEW_PADDING_INLINE,
	},
});

type SnippetPanelItemProps = {
	snippet: Snippet;
	contentId: string;
	spaceId: string;
	spaceKey: string;
};

const SnippetPreviewContainer = ({ snippet }: { snippet: Snippet }) => (
	<Box xcss={styles.preview}>
		<SnippetPreview
			snippet={snippet}
			dimensions={{
				width: `calc(${SNIPPET_PREVIEW_WIDTH}px - (${PREVIEW_PADDING_INLINE} * 2))`,
				maxHeight: `calc(${SNIPPET_PREVIEW_HEIGHT}px - (${PREVIEW_PADDING_BLOCK} * 2))`,
			}}
			renderWidth={EDITOR_PRE_SCALE_WIDTH}
			loadingComponent={<SnippetPreviewSkeleton />}
		/>
	</Box>
);

export const SnippetPanelItem = ({
	snippet,
	contentId,
	spaceId,
	spaceKey,
}: SnippetPanelItemProps) => {
	const { userId } = useSessionData();
	const { editorAPI } = useContext(EditorContextPanelContext);
	const intl = useIntl();

	const [isHovered, setIsHovered] = useState(false);
	const [isMenuOpen, setIsMenuOpen] = useState(false);

	const insertSnippet = useInsertSnippet(editorAPI);
	const snippetsActions = useSnippetsActions();
	const { fireButtonClickedAnalytic, fireFailedAnalytic } = useSnippetAnalytics(
		ANALYTICS_ORIGIN.SNIPPET_PANEL,
	);
	const { showPreview } = useSnippetPopupPreview();

	const handleMouseEnter = (event: React.MouseEvent) => {
		setIsHovered(true);
		showPreview(snippet, event.currentTarget.getBoundingClientRect());
	};
	const handleMouseLeave = () => setIsHovered(false);
	const handleMenuOpenChange = (isOpen: boolean) => setIsMenuOpen(isOpen);
	const onClick = () => {
		const success = insertSnippet(snippet);
		if (success) {
			snippetsActions.recordSnippetInsert(snippet.snippetId);
			fireButtonClickedAnalytic(ANALYTICS_UI_SUBJECT_ID.INSERT_SNIPPET, {
				userId: userId ?? undefined,
				scope: snippet.scope,
				snippetId: snippet.snippetId,
				isShared: snippet.isShared,
				timestamp: Date.now(),
			});
		} else {
			fireFailedAnalytic(ANALYTICS_OPERATIONAL_SUBJECT.INSERT_SNIPPET, {
				userId: userId ?? undefined,
				scope: snippet.scope,
				snippetId: snippet.snippetId,
				isShared: snippet.isShared,
				timestamp: Date.now(),
			});
		}
	};

	const showActions = isHovered || snippet.isFavorite || isMenuOpen;
	const showPanelActions = isHovered || isMenuOpen;

	return (
		<Box
			xcss={cx(styles.cardWrapper, isMenuOpen && styles.cardWrapperActive)}
			onMouseEnter={handleMouseEnter}
			onMouseLeave={handleMouseLeave}
		>
			<Pressable
				xcss={styles.cardButtonOverlay}
				onClick={onClick}
				testId={`snippet-card-button-${snippet.snippetId}`}
				aria-label={intl.formatMessage(messages.snippetCardAriaLabel, { name: snippet.name })}
			/>
			<Inline
				xcss={styles.cardContentLayer}
				alignInline="start"
				alignBlock="center"
				space="space.150"
			>
				<SnippetPreviewContainer snippet={snippet} />
				<SnippetPanelItemContent
					snippet={snippet}
					isHovered={isHovered}
					showActions={showActions}
					showPanelActions={showPanelActions}
					onMenuOpenChange={handleMenuOpenChange}
					contentId={contentId}
					spaceId={spaceId}
					spaceKey={spaceKey}
				/>
			</Inline>
		</Box>
	);
};
